import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { UserStateService } from 'src/app/auth/services/user-state.service';
import { AnalyticsFactory } from 'src/app/ajs-upgraded-providers';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { TemplateEditorService } from '../../services/template-editor.service';
import { EditorToolsLoggingService } from '../services/editor-tools-logging.service';

import {
  ImproveTextAction,
  ImproveTextRequest,
  ImproveTextResponse,
  ImproveTextTone,
  TextAiApiService,
} from './services/text-ai-api.service';

const SLIDE_IN_ANIMATION_NAME = 'slide-in-right';

/**
 * Label for the Text AI action across all surfaces (main button, contextual
 * toolbar, panel header, panel region). Whitespace-only counts as empty.
 */
export function getImproveWithAiLabel(value: string | undefined): string {
  return value?.trim() ? 'Improve with AI' : 'Generate with AI';
}

export type ImproveWithAiMessageType = 'default' | 'no-credits' | 'error';

interface ImproveWithAiActionBase {
  label: string;
}

interface ImproveWithAiPresetAction extends ImproveWithAiActionBase {
  id: ImproveTextAction;
  category: 'preset';
  icon?: string;
}

interface ImproveWithAiToneAction extends ImproveWithAiActionBase {
  id: ImproveTextTone;
  category: 'tone';
  emoji?: string;
}

interface ImproveWithAiCustomAction extends ImproveWithAiActionBase {
  id: 'custom';
  category: 'custom';
}

export type ImproveWithAiAction =
  | ImproveWithAiPresetAction
  | ImproveWithAiToneAction
  | ImproveWithAiCustomAction;

const TEXT_AI_ANALYTICS_EVENT_NAMES: Record<ImproveWithAiAction['id'], string> =
  {
    longer: 'AI Editor Text Longer',
    shorter: 'AI Editor Text Shorter',
    refine: 'AI Editor Text Refine',
    summarize_bullets: 'AI Editor Text Summarize to Bullets',
    fix_spelling: 'AI Editor Text Fix Spelling',
    translate_spanish: 'AI Editor Text Translate to Spanish',
    friendly: 'AI Editor Text Friendly',
    urgent: 'AI Editor Text Urgent',
    formal: 'AI Editor Text Formal',
    positive: 'AI Editor Text Positive',
    impactful: 'AI Editor Text Impactful',
    custom: 'AI Editor Text Custom',
  };

@Component({
  selector: 'template-component-text-ai',
  templateUrl: './text-ai.component.html',
  styleUrls: ['./text-ai.component.scss'],
  standalone: false,
})
export class TextAiComponent implements OnChanges, OnDestroy {
  @Input() shown: boolean;
  @Input() value: string;
  @Input() componentId: string;

  @Output() textGenerated = new EventEmitter<string>();

  @ViewChild('panel') panel: ElementRef<HTMLElement>;

  customPrompt: string = '';
  loadingActionId: ImproveWithAiAction['id'] | null = null;
  messageType: ImproveWithAiMessageType | null = null;
  sessionId: string | null = null;

  private activeRequestId = 0;
  private focusOnAnimationEnd: ((event: Event) => void) | null = null;

  readonly presetOptions: ImproveWithAiPresetAction[] = [
    { id: 'longer', label: 'Longer', category: 'preset', icon: 'edit-longer' },
    {
      id: 'shorter',
      label: 'Shorter',
      category: 'preset',
      icon: 'edit-shorter',
    },
    { id: 'refine', label: 'Refine', category: 'preset', icon: 'magic-wand' },
    {
      id: 'summarize_bullets',
      label: 'Summarize to bullets',
      category: 'preset',
      icon: 'bullets',
    },
    {
      id: 'fix_spelling',
      label: 'Fix spelling',
      category: 'preset',
      icon: 'spelling',
    },
    {
      id: 'translate_spanish',
      label: 'Translate to Spanish',
      category: 'preset',
      icon: 'language',
    },
  ];

  readonly toneOptions: ImproveWithAiToneAction[] = [
    { id: 'friendly', label: 'Friendly', category: 'tone', emoji: '😊' },
    { id: 'urgent', label: 'Urgent', category: 'tone', emoji: '⚠️️' },
    { id: 'formal', label: 'Formal', category: 'tone', emoji: '👔' },
    { id: 'positive', label: 'Positive', category: 'tone', emoji: '🎉' },
    { id: 'impactful', label: 'Impactful', category: 'tone', emoji: '💥' },
  ];

  constructor(
    private textAiApi: TextAiApiService,
    private userStateService: UserStateService,
    private analyticsFactory: AnalyticsFactory,
    private companyStateService: CompanyStateService,
    private editorToolsLogging: EditorToolsLoggingService,
    private templateEditorService: TemplateEditorService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if ('shown' in changes) {
      if (changes['shown'].currentValue === true) {
        this.sessionId = this.generateSessionId();
        this.showDefaultCreditsMessage();
        this.scheduleFocus();
      } else if (!changes['shown'].firstChange) {
        this.resetPanelState();
      }
    }
  }

  ngOnDestroy() {
    this.clearScheduledFocus();
    this.activeRequestId++;
  }

  get hasText(): boolean {
    return !!this.value?.trim();
  }

  get panelLabel(): string {
    return getImproveWithAiLabel(this.value);
  }

  canGenerateCustomPrompt(): boolean {
    return !!this.customPrompt.trim();
  }

  isActionLoading(actionId: ImproveWithAiAction['id']): boolean {
    return this.loadingActionId === actionId;
  }

  canAccessCompanySettings(): boolean {
    return (
      this.userStateService.isRiseAdmin() || this.userStateService.isUserAdmin()
    );
  }

  onActionClick(action: ImproveWithAiAction): Promise<void> {
    return this.runAction(action);
  }

  onCustomPromptGenerate(): Promise<void> | void {
    if (!this.canGenerateCustomPrompt()) {
      return;
    }

    return this.runAction({
      id: 'custom',
      label: 'Custom',
      category: 'custom',
    });
  }

  private async runAction(action: ImproveWithAiAction) {
    if (!this.sessionId) {
      return;
    }

    this.loadingActionId = action.id;
    this.showDefaultCreditsMessage();
    const requestId = ++this.activeRequestId;
    const startedAt = Date.now();
    const request = this.buildRequest(action);

    try {
      const response = await this.textAiApi.improveText(request);

      if (requestId !== this.activeRequestId) {
        return;
      }

      this.loadingActionId = null;

      if (response.type === 'no_credits') {
        this.messageType = 'no-credits';
        this.trackAction(action, 'no_credits', startedAt, response);
      } else {
        this.showDefaultCreditsMessage();
        this.trackAction(action, 'success', startedAt, response);
        this.logImprovement(action, request, response, startedAt);
        this.textGenerated.emit(response.data);
      }

      if (action.category === 'custom') {
        this.customPrompt = '';
      }
    } catch {
      if (requestId !== this.activeRequestId) {
        return;
      }

      this.loadingActionId = null;
      this.messageType = 'error';
      this.trackAction(action, 'error', startedAt);
    }
  }

  private buildRequest(action: ImproveWithAiAction): ImproveTextRequest {
    const base: ImproveTextRequest = {
      text: this.value,
      sessionId: this.sessionId!,
    };

    if (action.category === 'preset') {
      return { ...base, action: action.id };
    }
    if (action.category === 'tone') {
      return { ...base, tone: action.id };
    }
    return { ...base, customPrompt: this.customPrompt.trim() };
  }

  private trackAction(
    action: ImproveWithAiAction,
    status: 'success' | 'no_credits' | 'error',
    startedAt: number,
    response?: ImproveTextResponse,
  ) {
    this.analyticsFactory.track(TEXT_AI_ANALYTICS_EVENT_NAMES[action.id], {
      actionId: action.id,
      actionCategory: action.category,
      status,
      componentId: this.componentId,
      sessionId: this.sessionId,
      companyId: this.companyStateService.getSelectedCompanyId(),
      creditsUsed: response?.creditsUsed,
      durationMs: Date.now() - startedAt,
    });
  }

  private logImprovement(
    action: ImproveWithAiAction,
    request: ImproveTextRequest,
    response: ImproveTextResponse,
    startedAt: number,
  ) {
    if (response.type !== 'editor_ai_text' || !this.sessionId) {
      return;
    }

    this.editorToolsLogging
      .logTextImproved({
        sessionId: this.sessionId,
        presentationId: this.templateEditorService.presentation?.id,
        eventId: this.generateEventId(),
        prompt: request.customPrompt,
        actionId: action.id,
        actionCategory: action.category,
        textInput: request.text,
        textOutput: response.data,
        durationMs: Date.now() - startedAt,
        creditsUsed: response.creditsUsed,
      })
      .subscribe();
  }

  private scheduleFocus() {
    const el = this.panel?.nativeElement;
    if (!el) {
      return;
    }

    this.clearScheduledFocus();

    this.focusOnAnimationEnd = (event: Event) => {
      if ((event as AnimationEvent).animationName !== SLIDE_IN_ANIMATION_NAME) {
        return;
      }
      this.clearScheduledFocus();
      el.focus({ preventScroll: true });
    };
    el.addEventListener('animationend', this.focusOnAnimationEnd);
  }

  private clearScheduledFocus() {
    const el = this.panel?.nativeElement;
    if (el && this.focusOnAnimationEnd) {
      el.removeEventListener('animationend', this.focusOnAnimationEnd);
    }
    this.focusOnAnimationEnd = null;
  }

  private resetPanelState() {
    this.clearScheduledFocus();
    this.activeRequestId++;
    this.loadingActionId = null;
    this.customPrompt = '';
    this.messageType = null;
    this.sessionId = null;
  }

  private showDefaultCreditsMessage() {
    this.messageType = 'default';
  }

  private generateSessionId(): string {
    return (
      'session_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11)
    );
  }

  private generateEventId(): string {
    return 'text_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);
  }
}
